import SearchInput from "@/components/common/search-input";
import { useGetAvailableMembers } from "@/queries/approval-groups";
import { cn } from "@/utils/theme";
import {
  Avatar,
  AvatarFallback,
  Button,
  Icon,
} from "@elaniintc/goes-design-system";
import { DropdownMenuSeparator } from "@radix-ui/react-dropdown-menu";
import { useMemo, useState, useRef, useEffect } from "react";
import type { UserGroup } from ".";

interface Props {
  label: string;
  isDisabled?: boolean;
  name?: string;
  required?: boolean;
  selectedUser?: UserGroup;
  defaultUsers?: UserGroup[];
  onSelectUser: (user: UserGroup) => void;
  placeholder?: string;
  groupId?: string | null;
  formalityId?: string | null;
}

export const UserGroupSearchSelector = ({
  label,
  isDisabled = false,
  name = "user-search-selector",
  required = false,
  selectedUser,
  onSelectUser,
  defaultUsers,
  groupId,
  placeholder = "Buscar usuario...",
  formalityId,
}: Props) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const { data, isFetching } = useGetAvailableMembers(formalityId, groupId);

  const isLoading = !data;

  const _data = useMemo(() => {
    if (!data?.length) return [];

    const q = searchQuery.trim().toLowerCase();

    return (
      data
        .map((group) => {
          // Usuarios default a excluir SOLO para este grupo
          const defaultIdsInThisGroup = new Set(
            (defaultUsers ?? [])
              .filter((u) => u.group_id === group.group_id)
              .map((u) => u.id),
          );

          const matchedMembers = group.members.filter((user) => {
            // Excluir defaultUsers solo en este grupo
            const isNotDefaultUserInThisGroup = !defaultIdsInThisGroup.has(
              user.id,
            );

            // Excluir selectedUser solo si pertenece a este grupo
            const isNotSelectedUserInThisGroup =
              !selectedUser ||
              selectedUser.group_id !== group.group_id ||
              user.id !== selectedUser.id;

            const isAllowed =
              isNotDefaultUserInThisGroup && isNotSelectedUserInThisGroup;

            // Sin búsqueda → devolver todos los permitidos
            if (!q) return isAllowed;

            const isMatchingSearch =
              `${user.name} ${user.last_name}`.toLowerCase().includes(q) ||
              (user.email?.toLowerCase().includes(q) ?? false);

            return isAllowed && isMatchingSearch;
          });

          return {
            groupId: group.group_id,
            groupName: group.group_name,
            members: matchedMembers,
          };
        })
        // Mostrar solo grupos con coincidencias
        .filter((group) => group.members.length > 0)
    );
  }, [data, searchQuery, defaultUsers, selectedUser]);

  const apiEmpty = (data?.length || 0) === 0;

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleSelectUser = (user: UserGroup) => {
    onSelectUser(user);
    setSearchQuery("");
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <label
        className={cn(
          "font-semibold text-body-loud text-sm leading-2xl",
          isDisabled && "text-on-disabled!",
        )}
        htmlFor={name}
      >
        {label}{" "}
        {required && (
          <span
            className={cn(
              "text-sm text-danger",
              isDisabled && "text-on-disabled!",
            )}
          >
            *
          </span>
        )}
      </label>
      <div className="relative">
        <SearchInput
          ref={inputRef}
          className="w-full text-size-body!"
          variant="md"
          placeholder={placeholder}
          value={searchQuery}
          disabled={isDisabled}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            if (!isOpen) {
              setIsOpen(true);
            }
          }}
          onFocus={() => {
            setIsOpen(true);
          }}
        />
      </div>

      {isOpen && (
        <div className="absolute z-50 w-full mt-1 rounded-lg border border-neutral-silent text-body-loud font-semibold bg-neutral-object shadow-lg">
          <div className="max-h-55 overflow-auto pt-2">
            <Content
              isLoading={isLoading}
              _data={_data}
              isFetching={isFetching}
              hasNextPage={false}
              fetchNextPage={() => {}}
              isEmpty={_data.length === 0}
              apiEmpty={apiEmpty}
              onSelectUser={handleSelectUser}
            />
          </div>
        </div>
      )}

      <span className="text-body-quiet text-xs">
        El cambio ocurrirá en el grupo al que el usuario seleccionado pertenece.
      </span>
    </div>
  );
};

const Content = ({
  isLoading,
  _data,
  isFetching,
  hasNextPage,
  fetchNextPage,
  isEmpty,
  apiEmpty = false,
  onSelectUser,
}: {
  isLoading: boolean;
  apiEmpty?: boolean;
  isEmpty: boolean;
  hasNextPage: boolean;
  fetchNextPage: () => void;
  _data: {
    groupId: string;
    groupName: string;
    members: {
      id: string;
      name: string;
      last_name?: string | undefined;
      email?: string | undefined;
      seniority?: number | undefined;
    }[];
  }[];
  isFetching: boolean;
  onSelectUser: (user: UserGroup) => void;
}) => {
  if (isLoading) {
    return (
      <div className="w-full h-32 flex items-center justify-center">
        <Icon
          type="far"
          name="spinner-third"
          className="text-on-disabled animate-spin text-2xl"
        />
      </div>
    );
  }

  if (hasNextPage && _data.length === 0) {
    return (
      <div className="w-full h-32 flex items-center justify-center">
        <Button
          variant="ghost-blue"
          size="xs"
          type="button"
          disabled={isFetching}
          onClick={() => {
            if (isFetching) return;
            fetchNextPage();
          }}
        >
          Más resultados
          <Icon
            type="far"
            name={isFetching ? "spinner-third" : "plus"}
            className={cn("text-current", isFetching && "animate-spin")}
          />
        </Button>
      </div>
    );
  }

  if (apiEmpty) {
    return (
      <div className="w-full h-52.5 flex items-center justify-center">
        <span className="text-body-quiet font-normal">
          No hay usuarios disponibles
        </span>
      </div>
    );
  }

  if (isEmpty) {
    return (
      <div className="w-full h-52.5 flex items-center justify-center">
        <span className="text-body-quiet font-normal">
          No hay más resultados
        </span>
      </div>
    );
  }

  return (
    <>
      <ul className="px-2 min-h-32">
        {_data?.map((group, index) => (
          <div key={`${group.groupId}--${index}`}>
            <div className="flex items-center gap-2 px-3 py-1">
              <span className="text-xs text-body-quiet font-semibold  tracking-wide">
                <Icon type="fa" name="users" className="text-current mr-1" />{" "}
                {group.groupName}
              </span>
              <DropdownMenuSeparator className="border-neutral-quiet border-t flex-1" />
            </div>
            {group.members.map((user, userIndex) => {
              const initials = user.name ? `${user.name.charAt(0)}` : "";
              return (
                <li
                  key={`${user.id}--${userIndex}`}
                  className="flex items-center justify-between gap-3 p-2 hover:bg-neutral-object-accent-quiet rounded-lg cursor-pointer"
                  onClick={() =>
                    onSelectUser({
                      ...user,
                      group_id: group.groupId,
                      group_name: group.groupName,
                    } as UserGroup)
                  }
                >
                  <Avatar size="2xs">
                    <AvatarFallback
                      color="action"
                      className="pointer-events-none"
                    >
                      {initials}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 flex items-center gap-2 text-base leading-3xl truncate pointer-events-none select-none">
                    <span className="text-body font-semibold whitespace-nowrap">{`${user.name} ${user.last_name}`}</span>
                    <span className="text-body-quiet font-normal truncate">
                      {user.email || "-"}
                    </span>
                  </div>

                  <div className="pl-3">
                    <Icon
                      type="far"
                      name="plus"
                      className="text-current text-base leading-3xl pointer-events-none"
                    />
                  </div>
                </li>
              );
            })}
          </div>
        ))}
      </ul>
      {hasNextPage && (
        <div className="w-full border-t border-neutral-quiet p-3 flex items-center justify-start gap-3">
          <Button
            variant="ghost-blue"
            size="xs"
            type="button"
            disabled={isFetching}
            onClick={() => {
              if (isFetching) return;
              fetchNextPage();
            }}
          >
            Más resultados
            <Icon
              type="far"
              name={isFetching ? "spinner-third" : "plus"}
              className={cn("text-current", isFetching && "animate-spin")}
            />
          </Button>
        </div>
      )}
    </>
  );
};
